/**
 * SearchCombobox — Batch 4 Section C
 *
 * Reusable cascading-dropdown component built on top of:
 *   - `Popover` (Radix)
 *   - `Command` / `CommandInput` / `CommandItem` (cmdk via shadcn wrapper)
 *
 * Use everywhere the user must pick a single canonical value out of a
 * server-supplied list (brands, models, cities, postal codes, etc.).
 *
 * Why a wrapper rather than inline cmdk: cascading-dropdown UX needs
 *   1. Disabled state with greyed look + tooltip explaining why
 *   2. Loading skeleton while parent's options are still in flight
 *   3. Truncation hint when an option list is huge (PLZ → 7 415 items)
 *   4. A `count` badge per row ("BMW · 1 234")
 *   5. Consistent keyboard nav + ARIA
 *
 * API:
 *   <SearchCombobox
 *     value={selected}
 *     onChange={setSelected}
 *     options={[{ value: "bmw", label: "BMW", count: 1234 }]}
 *     placeholder="Marke wählen…"
 *     disabled={!country}
 *     disabledReason="Wähle zuerst ein Land"
 *     loading={false}
 *     emptyMessage="Keine Treffer"
 *     maxVisible={500}        // cmdk's perf falls off >1k; we slice + hint
 *     dataTestId="brand-picker"
 *   />
 *
 * onChange semantics: receives the option `value` (slug), not the label.
 *   Pass an empty string to clear (or use `clearable` prop with the X icon).
 *
 * Multi-vertical reuse: this component is content-agnostic. Same instance
 * powers Auto (brands/models/series/fuels) and Immo (cities/postal codes/
 * object types) — and later Watches / Boats / Motorcycles.
 */
import * as React from "react";
import { Check, ChevronsUpDown, X, Loader2 } from "lucide-react";

import i18n from "../../i18n";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * @typedef {Object} ComboboxOption
 * @property {string} value      Canonical slug — emitted via onChange
 * @property {string} label      Human-readable display name
 * @property {number} [count]    Optional listings count badge
 * @property {string} [hint]     Optional sub-label (e.g. PLZ for a city)
 */

/**
 * @param {Object} props
 * @param {string|null} props.value
 * @param {(v: string) => void} props.onChange
 * @param {ComboboxOption[]} props.options
 * @param {string} [props.placeholder]
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledReason]
 * @param {boolean} [props.loading]
 * @param {string} [props.emptyMessage]
 * @param {number} [props.maxVisible]   // hard cap on rendered items (perf)
 * @param {boolean} [props.clearable]
 * @param {string} [props.className]
 * @param {string} [props.dataTestId]
 */
export function SearchCombobox({
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  disabledReason = null,
  loading = false,
  emptyMessage,
  maxVisible = 500,
  clearable = true,
  className,
  dataTestId,
}) {
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value],
  );

  // Slice when the list is unwieldy. cmdk's matchSorter is O(n*m) on the
  // first render — fine up to ~1 k, sluggish at 7 k+. We render only the
  // first `maxVisible` and tell the user to type to narrow it down.
  const truncated = options.length > maxVisible;
  const visible = truncated ? options.slice(0, maxVisible) : options;

  const handleSelect = (v) => {
    onChange(v === value ? "" : v);
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
  };

  return (
    <Popover open={open && !disabled} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-disabled={disabled}
          disabled={disabled}
          title={disabled && disabledReason ? disabledReason : undefined}
          className={cn(
            "w-full h-10 justify-between font-normal rounded-lg",
            "border-zinc-800 bg-zinc-950/60 text-zinc-200",
            "hover:bg-zinc-900 hover:border-zinc-700 hover:text-zinc-100",
            "focus-visible:ring-2 focus-visible:ring-amber-500/50 focus-visible:border-amber-500/40",
            "data-[state=open]:border-amber-500/40 data-[state=open]:ring-2 data-[state=open]:ring-amber-500/30",
            "disabled:opacity-50 disabled:hover:bg-zinc-950/60 disabled:hover:border-zinc-800",
            "transition",
            !selected && "text-zinc-500",
            className,
          )}
          data-testid={dataTestId}
        >
          <span className="truncate text-left flex-1">
            {loading ? (
              <span className="inline-flex items-center gap-2 text-zinc-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                Lade…
              </span>
            ) : selected ? (
              <>
                <span className="text-zinc-100">{selected.label}</span>
                {typeof selected.count === "number" && (
                  <span className="ml-2 text-xs text-zinc-500">
                    · {selected.count.toLocaleString("de-AT")}
                  </span>
                )}
              </>
            ) : (
              placeholder
            )}
          </span>
          {clearable && selected && !disabled ? (
            <X
              role="button"
              aria-label={i18n.t("common:ui.clearSelection")}
              data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
              className="ml-2 h-4 w-4 shrink-0 text-zinc-500 hover:text-amber-300 transition"
              onClick={handleClear}
            />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-zinc-500" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "w-[--radix-popover-trigger-width] min-w-[220px] p-0 overflow-hidden",
          "rounded-xl border border-zinc-800 bg-zinc-950 text-zinc-100",
          "shadow-2xl shadow-black/60",
        )}
        align="start"
        sideOffset={6}
        data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
      >
        <Command className="bg-transparent text-zinc-100">
          <div className="border-b border-zinc-800 [&_[cmdk-input-wrapper]]:border-0 [&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]_svg]:text-zinc-500">
            <CommandInput
              placeholder={`${i18n.t("common:ui.search")}…`}
              className="text-zinc-100 placeholder:text-zinc-500 bg-transparent"
              data-testid={dataTestId ? `${dataTestId}-input` : undefined}
            />
          </div>
          <CommandList className="dark-scroll max-h-[280px] py-1">
            <CommandEmpty className="py-6 text-center text-sm text-zinc-500">
              {emptyMessage || i18n.t("common:ui.noResults")}
            </CommandEmpty>
            <CommandGroup className="px-1 py-0.5">
              {visible.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={`${opt.label} ${opt.value}`}
                  onSelect={() => handleSelect(opt.value)}
                  onClick={() => handleSelect(opt.value)}
                  data-testid={
                    dataTestId ? `${dataTestId}-option-${opt.value}` : undefined
                  }
                  className={cn(
                    "rounded-md px-2.5 py-2 my-0.5 text-sm cursor-pointer",
                    "text-zinc-300",
                    "data-[selected=true]:bg-amber-500/15 data-[selected=true]:text-amber-100",
                    "aria-selected:bg-amber-500/15 aria-selected:text-amber-100",
                    value === opt.value && "bg-amber-500/10 text-amber-100",
                  )}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 text-amber-400",
                      value === opt.value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 truncate">{opt.label}</span>
                  {opt.hint && (
                    <span className="ml-2 text-xs text-zinc-500 truncate">
                      {opt.hint}
                    </span>
                  )}
                  {typeof opt.count === "number" && (
                    <span className="ml-2 text-xs text-zinc-500">
                      {opt.count.toLocaleString("de-AT")}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            {truncated && (
              <div
                className="border-t border-zinc-800 px-3 py-2 text-xs text-zinc-500"
                data-testid={
                  dataTestId ? `${dataTestId}-truncation-hint` : undefined
                }
              >
                +{(options.length - maxVisible).toLocaleString("de-AT")} weitere
                — bitte suchen um zu verfeinern.
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchCombobox;
